import {useReachGoalCallback} from '@yandex-delivery/blocks'
import {useCallback} from 'react'
import {useFormContext} from 'react-hook-form'

import {useConfigContext} from '../contexts/ConfigContext'

type ReachGoalParameters = {
  isBusiness?: boolean
  UID?: string
  tariff?: string
}

export function useFormUserEventHandler() {
  const reachGoalCallback = useReachGoalCallback<ReachGoalParameters>()
  const config = useConfigContext()
  const {watch} = useFormContext()

  return useCallback(
    (goalName: EventGoal | string) => {
      reachGoalCallback(goalName, {
        isBusiness: watch('is_business'),
        tariff: watch('selected_submode'),
        UID: config.yandexuid,
      })
    },
    [config.yandexuid, reachGoalCallback, watch],
  )
}

export const enum EventGoal {
  // <Order>
  /**
   * Клик на активную кнопку "Заказать"
   * @see https://tariff-editor.taxi.tst.yandex-team.ru/experiments3/configs/show/cargo_c2c_order_forms_b2b_lk_cargo_order_button_widget/current?=&=&removed=without_removed&order=name_desc&show_used_as_experiment=all&showFavoritesOnly=false&is_technical=all&get_financial=all&active=all&enabled=all&name=cargo_c2c_order_forms_b2b_lk_cargo_order_button_widget
   */
  SubmitButtonClick = 'order2-button-click-request',
  /**
   * Клик на опцию Расчетный счет в блоке Оплата
   */
  B2BOfferClick = 'order2-button-click-offer',
  /**
   * Клик на опцию XXL в блоке Отправление
   * @see https://tariff-editor.taxi.tst.yandex-team.ru/experiments3/configs/edit/cargo_c2c_order_forms_b2b_lk_cargo_body_size_widget?=&=&removed=without_removed&order=name_desc&show_used_as_experiment=all&showFavoritesOnly=false&is_technical=all&get_financial=all&active=all&enabled=all&name=cargo_c2c_order_forms_b2b_lk_cargo_body_size_widget
   */
  B2BXXLSizeClick = 'order2-button-click-xxl-size',
  /**
   * Клик на кнопку "Зарегистрироваться" в карточке Оплата с РС
   */
  BannerRegistrationButtonClick = 'order2-button-click-reg-offer',
  /**
   * Клик на переключатель Бизнес
   */
  BusinessSwitchClick = 'order2-button-click-switcher',
  /**
   * Выбор адреса (пункта выдачи) из suggest в блоке откуда
   */
  AddressFromSuggestResultItemClick = 'order2-suggest-click-from-address',
  /**
   * Клик на раздел адрес (пункт приема) в блоке откуда
   */
  AddressFromSuggestClick = 'order2-button-click-from-address',
  /**
   * Клик на раздел получатель в блоке откуда
   */
  AddressFromPhoneClick = 'order2-button-click-from-initiator',
  /**
   * Выбор адреса (пункта выдачи) из suggest в блоке куда
   */
  AddressToSuggestResultItemClick = 'order2-suggest-click-to-address',
  /**
   * Клик на раздел адрес (пункт выдачи) в блоке куда
   */
  AddressToSuggestClick = 'order2-button-click-to-address',
  /**
   * клик на раздел ПОЛУЧАТЕЛЬ в блоке КУДА
   */
  AddressToPhoneClick = 'order2-button-click-to-initiator',
  /**
   * Клик на Сейчас/Ко времени в блоке когда
   * @see https://tariff-editor.taxi.tst.yandex-team.ru/experiments3/configs/show/cargo_c2c_order_forms_b2b_lk_cargo_due_switch_widget/current?=&=&removed=without_removed&order=name_desc&show_used_as_experiment=all&showFavoritesOnly=false&is_technical=all&get_financial=all&active=all&enabled=all&name=cargo_c2c_order_forms_b2b_lk_cargo_due_switch_widget
   */
  WhenDueClick = 'order2-button-click-when-due',
  /**
   * Выбор любой из опций в блоке когда
   * @see https://tariff-editor.taxi.tst.yandex-team.ru/experiments3/configs/show/cargo_c2c_order_forms_b2b_lk_cargo_offer_cargo_widget/current?=&=&removed=without_removed&order=name_desc&show_used_as_experiment=all&showFavoritesOnly=false&is_technical=all&get_financial=all&active=all&enabled=all&name=cargo_c2c_order_forms_b2b_lk_cargo_offer_cargo_widget
   */
  OfferSelect = 'order2-button-click-when-tariff',
  /**
   * Клик на кнопку "Изменить" в сроке карты в блоке Оплата
   */
  SelectPaymentCard = 'order2-button-click-pay-card',
  /**
   * Событие успешного заказа перед тем как уйти в route
   */
  SuccessOrderCreate = 'order2-button-click-successful-request',
  /**
   * Выбор города из suggest в блоке откуда
   */
  AddressCityFromSuggestResultItemClick = 'order2-suggest-click-from-address-city',
  /**
   * Клик на поле города в блоке откуда
   */
  AddressCityFromSuggestClick = 'order2-button-click-from-address-city',
  /**
   * Клик на поле пункта выдачи в блоке откуда
   */
  AddressPickupFromSuggestClick = 'order2-button-click-from-address-pickup',
  /**
   * Выбор пункта выдачи из suggest в блоке откуда
   */
  AddressPickupFromSuggestResultItemClick = 'order2-suggest-click-from-address-pickup',
  /**
   * Выбор города из suggest в блоке куда
   */
  AddressCityToSuggestResultItemClick = 'order2-suggest-click-to-address-city',
  /**
   * Клик на поле города в блоке куда
   */
  AddressCityToSuggestClick = 'order2-button-click-to-address-city',
  /**
   * Клик на поле пункта выдачи в блоке куда
   */
  AddressPickupToSuggestClick = 'order2-button-click-to-address-pickup',
  /**
   * Выбор пункта выдачи из suggest в блоке куда
   */
  AddressPickupToSuggestResultItemClick = 'order2-suggest-click-to-address-pickup',
  /**
   * Клик на кнопку входа в yandex ID в блоке оплата
   */
  PaymentSignInClick = 'order2-button-click-signin_buttom',
  /**
   * Событие успешного заказа на тарифе грузовой межгород
   */
  SuccessOrderCargoIntercityCreate = 'cargo-intercity-button-click-successful-request',
}
